/**
 * Language Context Provider
 *
 * TASK-167: Frontend i18n Infrastructure Setup
 *
 * Provides global language state management and syncs with:
 * - Account preferences (backend API)
 * - localStorage (fallback)
 * - Browser language detection (i18next)
 *
 * Features:
 * - Language switching with immediate UI update
 * - Persistence to backend if authenticated
 * - localStorage fallback when not authenticated
 * - Integration with AccountContext
 *
 * @module contexts/LanguageContext
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from './AccountContext';
import { httpClient } from '../lib/httpClient';

export type Language = 'en' | 'de' | 'fr' | 'it';

/**
 * Language context value interface
 */
export interface LanguageContextValue {
  /** Current active language */
  language: Language;

  /** Change the language and persist to backend/localStorage */
  setLanguage: (lang: Language) => Promise<void>;

  /** Available languages */
  availableLanguages: Language[];

  /** Whether current language is right-to-left (none of Swiss languages are RTL) */
  isRTL: boolean;
}

/**
 * Language context - provides language state to entire app
 */
const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

/**
 * Language Provider Props
 */
interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Language Provider Component
 *
 * Wraps the application to provide language context.
 * Must be nested inside AccountProvider.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AccountProvider>
 *       <LanguageProvider>
 *         <YourApp />
 *       </LanguageProvider>
 *     </AccountProvider>
 *   );
 * }
 * ```
 */
export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const { account } = useAccount();
  const [language, setLanguageState] = useState<Language>('en');
  const [isInitialized, setIsInitialized] = useState(false);

  const availableLanguages: Language[] = ['en', 'de', 'fr', 'it'];

  /**
   * Update HTML lang attribute for proper date/number formatting
   * Maps language codes to Swiss/European locales for European formatting (DD.MM.YYYY)
   */
  useEffect(() => {
    const localeMap: Record<Language, string> = {
      de: 'de-CH', // German-Swiss: DD.MM.YYYY
      fr: 'fr-CH', // French-Swiss: DD.MM.YYYY
      it: 'it-CH', // Italian-Swiss: DD.MM.YYYY
      en: 'en-GB'  // British English: DD/MM/YYYY (European format, not US MM/DD/YYYY)
    };

    const locale = localeMap[language];
    document.documentElement.lang = locale;

    if (import.meta.env.MODE === 'development') {
      console.log(`🌍 Updated HTML lang to ${locale} for European date format`);
    }
  }, [language]);

  /**
   * Load language on mount and when account changes
   *
   * Priority order:
   * 1. Account.preferred_language (if authenticated)
   * 2. localStorage.getItem('i18nextLng')
   * 3. Browser language detection (handled by i18next)
   * 4. Fallback to 'en'
   */
  useEffect(() => {
    if (account && account.preferred_language) {
      // Load from account preference (if authenticated and has preference)
      const accountLanguage = account.preferred_language as Language;
      if (availableLanguages.includes(accountLanguage)) {
        if (language !== accountLanguage) {
          setLanguageState(accountLanguage);
        }
        // TASK-607: sync i18next INDEPENDENTLY of the React state. The old
        // combined guard skipped changeLanguage when state already matched the
        // account — leaving i18next on the browser-detected language (German
        // copy under an active EN flag, the audit's Settings screenshot).
        if (i18n.language.split('-')[0] !== accountLanguage) {
          i18n.changeLanguage(accountLanguage);
        }

        if (import.meta.env.MODE === 'development') {
          console.log('[LanguageContext] Loaded language from account:', accountLanguage);
        }
      }
      setIsInitialized(true);
    } else if (!isInitialized) {
      // Load from localStorage (fallback) - only on first mount
      const storedLang = localStorage.getItem('i18nextLng') as Language;
      if (storedLang && availableLanguages.includes(storedLang)) {
        setLanguageState(storedLang);

        if (import.meta.env.MODE === 'development') {
          console.log('[LanguageContext] Loaded language from localStorage:', storedLang);
        }
      } else {
        // i18next will use browser detection, update our state to match
        const currentLang = i18n.language.split('-')[0] as Language; // 'en-US' -> 'en'
        if (availableLanguages.includes(currentLang)) {
          setLanguageState(currentLang);
        } else {
          setLanguageState('en'); // Final fallback
        }
      }
      setIsInitialized(true);
    }
  }, [account?.preferred_language]); // Only depend on the specific value, not the whole account object

  /**
   * Set language and persist
   *
   * Flow:
   * 1. Update local state immediately (optimistic update)
   * 2. Change i18next language (updates UI)
   * 3. Save to localStorage
   * 4. If authenticated, persist to backend via API
   *
   * @param newLang - Language code to switch to
   */
  const setLanguage = async (newLang: Language): Promise<void> => {
    try {
      // Validate language
      if (!availableLanguages.includes(newLang)) {
        console.warn(`[LanguageContext] Invalid language: ${newLang}, falling back to 'en'`);
        newLang = 'en';
      }

      // Update UI immediately (optimistic update)
      setLanguageState(newLang);
      await i18n.changeLanguage(newLang);

      // Persist to localStorage
      localStorage.setItem('i18nextLng', newLang);

      // Persist to backend if authenticated
      if (account) {
        try {
          await httpClient.put('/api/settings/language', { language: newLang });

          if (import.meta.env.MODE === 'development') {
            console.log('[LanguageContext] Language persisted to backend:', newLang);
          }
        } catch (apiError) {
          // Silent failure - language still changed in UI and localStorage
          console.warn('[LanguageContext] Failed to persist language to backend:', apiError);
          // Don't throw - this is acceptable degradation
        }
      }
    } catch (error) {
      console.error('[LanguageContext] Failed to set language:', error);
      // Don't rethrow - we don't want to block the UI
    }
  };

  const value: LanguageContextValue = {
    language,
    setLanguage,
    availableLanguages,
    isRTL: false, // None of the Swiss languages (en, de, fr, it) are RTL
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Use Language Hook
 *
 * Access language context from any component.
 *
 * @throws Error if used outside LanguageProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { language, setLanguage, availableLanguages } = useLanguage();
 *
 *   return (
 *     <div>
 *       <p>Current language: {language}</p>
 *       <button onClick={() => setLanguage('de')}>Switch to German</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      'useLanguage must be used within LanguageProvider. ' +
      'Make sure <LanguageProvider> wraps your app in App.tsx'
    );
  }

  return context;
}

/**
 * Export LanguageContext for advanced use cases
 * (e.g., using Context.Consumer directly)
 */
export { LanguageContext };
